import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ArrowRight } from 'lucide-react';

const solutions = [
  { title: 'Photogrammetry', link: '/photogrammetry' },
  { title: 'Thermal Imaging', link: '/thermal-imaging' },
  { title: 'LiDAR Mapping', link: '/lidar-mapping' },
  { title: 'Multispectral Imaging', link: '/multispectral-imaging' },
];

const industries = [
  { title: 'Renewable Energy', link: '/industries' },
  { title: 'Oil & Gas', link: '/industries' },
  { title: 'Mining & Resources', link: '/industries' },
  { title: 'Power & Utilities', link: '/industries' },
  { title: 'Construction & Real Estate', link: '/industries' },
  { title: 'Telecommunications', link: '/industries' },
  { title: 'Transportation', link: '/industries' },
  { title: 'Agriculture & Forestry', link: '/industries' },
];

interface FullScreenMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

function AccordionSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 2.5, px: 1, cursor: 'pointer',
          '&:hover .acc-title': { color: '#E80029' },
        }}
      >
        <Typography className="acc-title" sx={{
          fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.1em',
          textTransform: 'uppercase', fontFamily: 'Geomanist, sans-serif',
          color: '#000', transition: 'color 0.3s',
        }}>
          {title}
        </Typography>
        <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown size={18} color="#000" />
        </motion.div>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pb: 2, pl: 1 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function MobileMenuItem({ title, to, onClose, disabled }: { title: string; to?: string; onClose: () => void; disabled?: boolean }) {
  const navigate = useNavigate();

  if (disabled) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1 }}>
        <Box sx={{ width: '10px', height: '1px', bgcolor: 'rgba(0,0,0,0.1)' }} />
        <Typography sx={{
          fontSize: '13px', fontFamily: 'Geomanist, sans-serif',
          fontStyle: 'italic', color: 'rgba(0,0,0,0.3)',
        }}>
          {title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => { navigate(to!); onClose(); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1,
        cursor: 'pointer',
        transition: 'background 0.2s',
        '&:hover': { bgcolor: 'rgba(232,0,41,0.05)' },
        '&:hover .item-title': { color: '#E80029' },
        '&:hover .item-dash': { bgcolor: '#E80029', width: '16px' },
      }}
    >
      <Box className="item-dash" sx={{ width: '10px', height: '1px', bgcolor: 'rgba(0,0,0,0.15)', transition: 'all 0.3s' }} />
      <Typography className="item-title" sx={{
        fontSize: '13px', fontFamily: 'Geomanist, sans-serif',
        color: 'rgba(0,0,0,0.7)', transition: 'color 0.3s',
      }}>
        {title}
      </Typography>
    </Box>
  );
}

function DirectLink({ title, to, onClose }: { title: string; to: string; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <Box
      onClick={() => { navigate(to); onClose(); }}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 2.5, px: 1, cursor: 'pointer',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        '&:hover .direct-title': { color: '#E80029' },
      }}
    >
      <Typography className="direct-title" sx={{
        fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.1em',
        textTransform: 'uppercase', fontFamily: 'Geomanist, sans-serif',
        color: '#000', transition: 'color 0.3s',
      }}>
        {title}
      </Typography>
      <ArrowRight size={16} color="rgba(0,0,0,0.3)" />
    </Box>
  );
}

export default function FullScreenMenu({ isOpen, onClose }: FullScreenMenuProps) {
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1198,
            backgroundColor: '#FFFFFF',
            overflowY: 'auto',
          }}
        >
          <Box sx={{ height: '88px' }} />

          <Box sx={{ position: 'absolute', top: 22, right: 16 }}>
            <IconButton onClick={onClose} sx={{ color: '#000' }}>
              <X size={24} />
            </IconButton>
          </Box>

          <Box sx={{ px: 4, py: 2, maxWidth: '480px', mx: 'auto' }}>
            {/* Solutions */}
            <AccordionSection title="Solutions">
              {solutions.map((s) => (
                <MobileMenuItem key={s.title} title={s.title} to={s.link} onClose={onClose} />
              ))}
              <Box
                onClick={() => { navigate('/solutions'); onClose(); }}
                sx={{
                  mt: 1, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                  '&:hover .see-all': { color: '#E80029' },
                }}
              >
                <Typography className="see-all" sx={{
                  fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase',
                  fontFamily: 'Geomanist, sans-serif', fontWeight: 'bold',
                  color: 'rgba(0,0,0,0.4)', transition: 'color 0.3s',
                }}>
                  View All Solutions
                </Typography>
                <ArrowRight size={12} color="rgba(0,0,0,0.3)" />
              </Box>
            </AccordionSection>

            {/* Industries */}
            <AccordionSection title="Industries">
              {industries.map((ind) => (
                <MobileMenuItem key={ind.title} title={ind.title} to={ind.link} onClose={onClose} />
              ))}
            </AccordionSection>

            {/* Direct links */}
            <DirectLink title="Portfolio" to="/portfolio" onClose={onClose} />
            <DirectLink title="Contact" to="/contact" onClose={onClose} />

            {/* Login CTA */}
            <Box sx={{ mt: 4 }}>
              <Box
                onClick={() => { navigate('/login'); onClose(); }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 48, bgcolor: '#E80029', cursor: 'pointer',
                  transition: 'background 0.3s',
                  '&:hover': { bgcolor: '#c50023' },
                }}
              >
                <Typography sx={{
                  fontSize: '12px', letterSpacing: '0.2em', fontWeight: 'bold',
                  textTransform: 'uppercase', fontFamily: 'Geomanist, sans-serif',
                  color: '#fff',
                }}>
                  Login
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}